import {
  Alert,
  AlertIcon,
  Box,
  Center,
  Heading,
  Spinner,
  Text,
  Flex,
  Image,
  Stack,
  HStack,
  Icon,
  VStack,
  Wrap,
  Tag,
} from "@chakra-ui/react";
import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useFocusOnLngLat } from "../../hooks/useFocusOnLngLat";
import HybridBottomSheetDrawer from "../../components/HybridBottomSheetDrawer";
import BackButton from "../../components/BackButton";
import { WelcomeWeekEvent } from "../../types/welcomeWeekTypes";
import { getSelectedEvent } from "../../data/welcomeEvents.api";
import { FaCalendarAlt } from "react-icons/fa";
import { MdLocationOn, MdOutlineAccessTimeFilled } from "react-icons/md";
import DirectionsButton from "../../components/search/DirectionsButton";

const ViewWelcomeEvent = () => {
  const { eventID, latitude, longitude } = useParams<{
    eventID: string;
    latitude: string;
    longitude: string;
  }>();
  const [event, setEvent] = useState<WelcomeWeekEvent>();
  const [loading, setLoading] = useState<boolean>(true); // Loading state
  const [error, setError] = useState<string | null>(null); // Error state
  const iconColor = "#48B8AC";

  useEffect(() => {
    async function fetchSelectedEvent() {
      setLoading(true); // Set loading to true before starting data fetching
      setError(null); // Reset error before fetching
      try {
        const selectedID = eventID?.includes("-") ? eventID : `#${eventID}`;
        const eventLocated = latitude !== "0" && longitude !== "0";
        const selectedEvent = getSelectedEvent(
          selectedID ? selectedID : "",
          eventLocated
        );
        setEvent(await selectedEvent);
      } catch (error) {
        setError("Failed to load events data");
      } finally {
        setLoading(false); // Stop loading after fetch attempt
      }
    }
    fetchSelectedEvent();
  }, []);

  useFocusOnLngLat(
    longitude === "0" ? undefined : Number(longitude),
    latitude === "0" ? undefined : Number(latitude),
    undefined,
    17.5
  );

  return (
    <HybridBottomSheetDrawer ml={"4em"}>
      {loading ? (
        <Center p={8}>
          <Spinner />
        </Center>
      ) : error ? (
        <Center p={8}>
          <Alert status="error">
            <AlertIcon />
            Could not find a event with ID {eventID}
          </Alert>
        </Center>
      ) : event ? (
        <>
          <Box position="relative" p={10}>
            <BackButton />

            <Box mb={3}>
              <VStack align="left" spacing={4}>
                <Heading size="lg" as="h1">
                  {event.description}
                </Heading>
                <Text wordBreak={"break-word"} fontSize="lg">
                  <Text
                    wordBreak={"break-word"}
                    fontSize="lg"
                    dangerouslySetInnerHTML={{ __html: event.long_description }}
                  />
                </Text>

                <VStack align="left" spacing={2}>
                  <HStack spacing={3}>
                    <Icon
                      as={MdOutlineAccessTimeFilled}
                      fontSize="1.5em"
                      color={iconColor}
                    />
                    <Text fontSize="lg">
                      {event?.utcStart.substring(11, 16)}
                    </Text>
                  </HStack>
                  <HStack spacing={3}>
                    <Icon
                      as={FaCalendarAlt}
                      fontSize="1.5em"
                      color={iconColor}
                    />
                    <Text fontSize="lg">
                      {event?.utcStart.substring(0, 10)}
                    </Text>
                  </HStack>
                  <HStack spacing={3}>
                    <Icon
                      as={MdLocationOn}
                      fontSize="1.5em"
                      color={iconColor}
                    />
                    <Text fontSize="lg">
                      {event?.location?.[0]?.name ?? "N/A"}
                    </Text>
                  </HStack>
                </VStack>

                {/* Checks for empty strings or spaces before rendering tags to account for bad data*/}
                {event.categories &&
                  event.categories.filter((tag) => tag.value?.trim()).length >
                    0 && (
                    <Wrap spacing={3} pt={4}>
                      {event.categories
                        .filter((tag) => tag.value?.trim())
                        .map((tag) => (
                          <Tag
                            key={tag.value}
                            size={"md"}
                            variant={"solid"}
                            colorScheme={"blue"}
                          >
                            {tag.value}
                          </Tag>
                        ))}
                    </Wrap>
                  )}
              </VStack>
            </Box>
            {event.location ? (
              <DirectionsButton
                lng={Number(longitude)}
                lat={Number(latitude)}
              />
            ) : null}
          </Box>
        </>
      ) : null}
    </HybridBottomSheetDrawer>
  );
};

export default ViewWelcomeEvent;
